import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { TerraceFormValues } from '../types';
import { GoogleMapsApiResponse, Coordinates, PhotoCacheResponse } from '@/types/api';
import { logger } from '@/lib/utils/logger';

const componentLogger = logger.forComponent('GoogleMapsProcessing');

export const useGoogleMapsProcessing = (
  form: UseFormReturn<TerraceFormValues>,
  populateFromApiData: (data: GoogleMapsApiResponse) => void,
  resetToDefaults: () => void
) => {
  const [isProcessingLink, setIsProcessingLink] = useState(false);
  const [isAiFilled, setIsAiFilled] = useState(false);
  const [extractedCoordinates, setExtractedCoordinates] = useState<Coordinates | null>(null);
  const [extractedPlaceId, setExtractedPlaceId] = useState<string | null>(null);
  const [extractedPlaceName, setExtractedPlaceName] = useState<string | null>(null);
  const [originalMapsUrl, setOriginalMapsUrl] = useState<string | null>(null);

  const handlePhotoCaching = async (data: GoogleMapsApiResponse): Promise<void> => {
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { data: cachedData, error } = await supabase.functions.invoke<PhotoCacheResponse>('cache-place-photo', {
        body: { photoReference: data.photoReference }
      });
      
      if (!error && cachedData?.cachedUrl) {
        componentLogger.info("Photo cached successfully", { cachedUrl: cachedData.cachedUrl });
        populateFromApiData({ ...data, photoUrl: cachedData.cachedUrl });
      } else {
        componentLogger.error("Failed to cache photo", { error });
        populateFromApiData(data);
      }
    } catch (error) {
      componentLogger.error("Photo caching error", {}, error as Error);
      populateFromApiData(data);
    }
  };

  const handleLinkProcessed = (success: boolean, data?: GoogleMapsApiResponse) => {
    if (success && data) {
      componentLogger.info("Processing API response", { 
        hasCoordinates: !!data.coordinates, 
        hasPlaceId: !!data.place_id,
        hasPhotoReference: !!data.photoReference
      });

      // Always store the original maps URL received from the API response
      if (data.originalMapsUrl) {
        setOriginalMapsUrl(data.originalMapsUrl);
      }

      // Store the extracted place name for photo processing
      setExtractedPlaceName(data.name);

      // Handle coordinates extraction and validation
      if (data.coordinates && data.coordinates.lat && data.coordinates.lng) {
        const coords: Coordinates = {
          lat: Number(data.coordinates.lat),
          lng: Number(data.coordinates.lng)
        };
        
        if (!isNaN(coords.lat) && !isNaN(coords.lng) &&
            coords.lat !== 0 && coords.lng !== 0 &&
            Math.abs(coords.lat) <= 90 && Math.abs(coords.lng) <= 180) {
          componentLogger.debug("Valid coordinates extracted", coords);
          setExtractedCoordinates(coords);
        } else {
          componentLogger.warn("Invalid coordinates received", { coordinates: coords });
          setExtractedCoordinates(null);
        }
      } else {
        componentLogger.warn("No coordinates in API response");
        setExtractedCoordinates(null);
      }

      // Extract place_id if available
      if (data.place_id) {
        componentLogger.debug("Place ID extracted", { placeId: data.place_id });
        setExtractedPlaceId(data.place_id);
      } else {
        componentLogger.debug("No place ID in response");
        setExtractedPlaceId(null);
      }

      // Handle photo reference if available
      if (data.photoReference) {
        componentLogger.info("Photo reference found, caching photo");
        handlePhotoCaching(data);
      } else {
        populateFromApiData(data);
      }

      setIsAiFilled(true);
      setIsProcessingLink(false);
    } else {
      setIsProcessingLink(false);
      setIsAiFilled(false);
    }
  };

  const handleResetForm = () => {
    setIsAiFilled(false);
    setExtractedCoordinates(null);
    setExtractedPlaceId(null);
    setExtractedPlaceName(null);
    setOriginalMapsUrl(null);
    resetToDefaults();
  };

  return {
    isProcessingLink,
    setIsProcessingLink,
    isAiFilled,
    extractedCoordinates,
    extractedPlaceId,
    extractedPlaceName,
    originalMapsUrl,
    handleLinkProcessed,
    handleResetForm
  };
};